import * as React from 'react';

import { graphql } from '@apollo/client/react/hoc';
import { snakeCase } from 'lodash';
import type { RouteComponentProps } from 'react-router-dom';

import './AuthenticationProviderSelection.scss';

import type { Query } from 'common/apollo/types';
import GlobalLoader from 'common/components/GlobalLoader/GlobalLoader';
import { CommonIdps } from 'common/constants/commonIdps';
import { compose } from 'common/helpers/fnHelpers';
import Icons from 'common/icons';

import AuthenticationProviderButton from './AuthenticationProviderButton/AuthenticationProviderButton';
import type { AuthenticationProvidersResponse, UriInfoResponse } from './queries';
import { AuthenticationProvidersQuery, UriInfoQuery } from './queries';
import UnauthenticatedSatUserMessage from './UnauthenticatedSatUserMessage/UnauthenticatedSatUserMessage';

export interface AuthenticationProviderSelectionProps
  extends RouteComponentProps<{
    customerUrlSlug?: string;
    organizationUrlSlug?: string;
    accountUrlSlug?: string;
  }> {
  AuthenticationProvidersQuery: Query<AuthenticationProvidersResponse>;
  UriInfoQuery: Query<UriInfoResponse>;
}

/**
 * AuthenticationProviderSelection component
 *
 * Displays page where IDP can be selected
 */
class AuthenticationProviderSelection extends React.PureComponent<AuthenticationProviderSelectionProps> {
  getProviderDisplayName = (name: string) => {
    if (name === 'Frame') {
      return 'email and password';
    }

    return name;
  };

  render() {
    const params = new URLSearchParams(this.props.location.search);
    const idp = params.get('idp');

    if (idp) {
      if (idp === CommonIdps.SAT_PROVIDER) {
        const secureAnonLoginUrl = params.get('satLoginUrl');
        if (secureAnonLoginUrl) {
          window.location.href = secureAnonLoginUrl;
          return <GlobalLoader />;
        }
        return <UnauthenticatedSatUserMessage />;
      }
      const returnUrl = encodeURIComponent(window.location.href);
      const { uriInfo } = this.props.UriInfoQuery;

      if (uriInfo) {
        const { imgUrl } = uriInfo;
        window.location.href = `${imgUrl}login?account_type=${idp}&return_url=${returnUrl}`;
      }
      return <GlobalLoader />;
    }
    const { authenticationProviders, loading } = this.props.AuthenticationProvidersQuery;

    if (loading) {
      return <GlobalLoader />;
    }

    if (authenticationProviders && authenticationProviders.length === 1) {
      window.location.href = authenticationProviders[0].redirectUrl;
      return <GlobalLoader />;
    }

    return (
      <div className="authentication-provider-selection">
        <div className="authentication-provider-selection__content">
          <img src={Icons.NUTANIX_TYPE_DARK} alt="Nutanix" className="authentication-provider-selection__logo" />

          {authenticationProviders &&
            authenticationProviders.map((provider: { name: string; redirectUrl: string; description: string }) => (
              <AuthenticationProviderButton
                key={provider.name}
                data-test={provider.name}
                name={provider.description ? provider.description : this.getProviderDisplayName(provider.name)}
                iconUrl={Icons.IDPs[provider.name.replace(' ', '_').toUpperCase()] || Icons.IDPs.SAML}
                redirectUrl={provider.redirectUrl}
              />
            ))}
        </div>

        <div
          className="authentication-provider-selection__background"
          style={{ backgroundImage: 'url(/backgrounds/1.jpg' }}
        />
      </div>
    );
  }
}

export default compose(
  // Query.
  graphql(AuthenticationProvidersQuery, {
    name: 'AuthenticationProvidersQuery',
    options: (ownProps: AuthenticationProviderSelectionProps) => {
      const { customerUrlSlug, organizationUrlSlug, accountUrlSlug } = ownProps.match.params;

      const params = {
        returnUrl: window.location.href,
        customerUrlSlug,
        organizationUrlSlug,
        accountUrlSlug,
      };

      /**
       * Generate query params from params
       * @type {string}
       */
      let encodedParams = '';
      if (typeof params === 'object') {
        Object.keys(params).forEach(key => {
          if (params[key]) {
            encodedParams += `${snakeCase(key)}=${encodeURIComponent(params[key])}&`;
          }
        });
      }

      return {
        variables: {
          params: encodedParams,
        },
      };
    },
  }),

  graphql(UriInfoQuery, {
    name: 'UriInfoQuery',
  })
)(AuthenticationProviderSelection);
